import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Send, Mail, MapPin, Phone, CheckCircle2, MessageSquare, Sparkles, ArrowRight } from 'lucide-react';
import { messagesService } from '../lib/firebase';

export default function Contact({ setToast }: { setToast?: (t: any) => void }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // 1. Always save to Firebase for internal tracking
      await messagesService.sendMessage(formData);
      
      // 2. If Formspree is configured, send there too
      const formspreeId = import.meta.env.VITE_FORMSPREE_ID;
      
      if (formspreeId && formspreeId.trim() !== '') {
        const response = await fetch(`https://formspree.io/f/${formspreeId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            _subject: `New Message from QuicklyUtils: ${formData.subject}`
          })
        });

        if (!response.ok) {
          console.warn('Formspree returned an error:', response.statusText);
        }
      } else {
        console.info('Contact Form: Formspree ID missing, skipping email transmission. Save to Firebase only.');
      }

      setSubmitted(true);
      if (setToast) {
        setToast({ message: "Message sent! We'll be in touch.", type: 'success' });
      }
    } catch (error) {
      console.error('Submit error:', error);
      if (setToast) {
        setToast({ message: "Error sending message. Please try again.", type: 'info' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="p-6 md:p-20 max-w-6xl mx-auto w-full"
    >
      <div className="flex flex-col lg:flex-row gap-20">
        {/* Left Side: Info */}
        <div className="flex-1">
          <header className="mb-12">
            <div className="flex items-center gap-3 text-accent mb-6">
              <Sparkles className="w-6 h-6" />
              <span className="font-black uppercase tracking-[0.3em] text-xs">Get in Touch</span>
            </div>
            <h1 className="text-6xl font-black tracking-tighter dark:text-white leading-[0.9] mb-8 italic">
              Connect with <br />
              the <span className="text-accent underline">Engine</span>.
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-lg leading-relaxed font-medium uppercase-none">
              Have a request, partnership inquiry, or just want to talk tech? Our lines are always open for the community.
            </p>
          </header>

          <div className="space-y-8">
            <div className="flex items-center gap-6 group">
              <div className="w-14 h-14 bg-white dark:bg-slate-900 border border-line dark:border-slate-800 rounded-2xl flex items-center justify-center text-slate-400 group-hover:text-accent group-hover:border-accent transition-all shadow-sm">
                <Mail className="w-6 h-6" />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Email us</p>
                <a href="mailto:contact@quicklyutils.com" className="text-lg font-bold dark:text-white hover:text-accent transition-colors">contact@quicklyutils.com</a>
              </div>
            </div>

            <div className="flex items-center gap-6 group">
              <div className="w-14 h-14 bg-white dark:bg-slate-900 border border-line dark:border-slate-800 rounded-2xl flex items-center justify-center text-slate-400 group-hover:text-accent group-hover:border-accent transition-all shadow-sm">
                <MapPin className="w-6 h-6" />
              </div>
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">HQ Location</p>
                <p className="text-lg font-bold dark:text-white">Somewhere in the Cloud (100% Online)</p>
              </div>
            </div>

            <div className="p-8 bg-slate-900 rounded-[2.5rem] mt-12 relative overflow-hidden group">
               <div className="relative z-10">
                 <h4 className="text-white font-bold text-lg mb-2">Live Support Status</h4>
                 <div className="flex items-center gap-2 mb-4">
                   <div className="w-2 h-2 bg-emerald-400 rounded-full animate-ping" />
                   <span className="text-emerald-400 text-[10px] font-black uppercase tracking-widest">Active & Online</span>
                 </div>
                 <p className="text-slate-400 text-xs leading-relaxed uppercase-none">Average response time: &lt; 24 hours.</p>
               </div>
               <MessageSquare className="absolute -right-4 -bottom-4 w-24 h-24 text-white/10 group-hover:scale-110 transition-transform rotate-[-12deg] select-none pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Right Side: Form */}
        <div className="flex-1">
          <AnimatePresence mode="wait">
            {!submitted ? (
              <motion.div 
                key="form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white dark:bg-slate-900 border border-line dark:border-slate-800 p-10 md:p-12 rounded-[3rem] shadow-2xl relative"
              >
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Full Name</label>
                      <input 
                        type="text" 
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        className="w-full bg-slate-50 dark:bg-slate-800/50 border border-line dark:border-slate-800 rounded-2xl py-4 px-6 text-sm outline-none focus:ring-2 focus:ring-accent/10 focus:border-accent transition-all dark:text-white"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Email Address</label>
                      <input 
                        type="email" 
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        className="w-full bg-slate-50 dark:bg-slate-800/50 border border-line dark:border-slate-800 rounded-2xl py-4 px-6 text-sm outline-none focus:ring-2 focus:ring-accent/10 focus:border-accent transition-all dark:text-white"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Subject</label>
                    <input 
                      type="text" 
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      className="w-full bg-slate-50 dark:bg-slate-800/50 border border-line dark:border-slate-800 rounded-2xl py-4 px-6 text-sm outline-none focus:ring-2 focus:ring-accent/10 focus:border-accent transition-all dark:text-white"
                      placeholder="Tool integration, feedback, etc."
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 ml-1">Message Detail</label>
                    <textarea 
                      required
                      rows={5}
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full bg-slate-50 dark:bg-slate-800/50 border border-line dark:border-slate-800 rounded-2xl py-4 px-6 text-sm outline-none focus:ring-2 focus:ring-accent/10 focus:border-accent transition-all dark:text-white resize-none"
                      placeholder="Write your message here..."
                    />
                  </div>

                  <button 
                    type="submit"
                    disabled={loading}
                    className="w-full bg-accent text-white py-5 rounded-2xl font-black text-sm hover:opacity-90 transition-all flex items-center justify-center gap-3 shadow-xl shadow-accent/20 group"
                  >
                    {loading ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        Invoke Message <Send className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                      </>
                    )}
                  </button>
                </form>
              </motion.div>
            ) : (
              <motion.div 
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white dark:bg-slate-900 border border-line dark:border-slate-800 p-20 rounded-[3rem] shadow-2xl text-center items-center flex flex-col justify-center h-full"
              >
                <div className="w-24 h-24 bg-emerald-500/10 text-emerald-500 rounded-full flex items-center justify-center mb-8">
                  <CheckCircle2 className="w-12 h-12" />
                </div>
                <h3 className="text-3xl font-black mb-4 dark:text-white tracking-tight italic">Transmission Received.</h3>
                <p className="text-slate-500 dark:text-slate-400 text-sm max-w-xs mx-auto mb-10 uppercase-none leading-relaxed">
                  Your message has been successfully broadcast to our engineering team. We'll get back to you shortly.
                </p>
                <button 
                  onClick={() => setSubmitted(false)}
                  className="px-10 py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center gap-2"
                >
                  Send another <ArrowRight className="w-4 h-4" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
}
